import classNames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';

import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';

import { type APIRequest } from '../types';
import { generateCurlCommand } from '../utils';
import { DataSection } from './DataSection';
import { DataTableWithDetails } from './DataTableWithDetails';

type APIRequestsTabProps = {
  apiRequests: APIRequest[];
  leftPanelWidth: number;
  setLeftPanelWidth: (width: number) => void;
  setApiRequests: (requests: APIRequest[]) => void;
  onCopy: (text?: string) => void;
};

/**
 * API Requests tab showing captured network requests with details panel.
 */
export const APIRequestsTab = memo(
  ({ apiRequests, leftPanelWidth, setLeftPanelWidth, setApiRequests, onCopy }: APIRequestsTabProps) => {
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
    const [requestFilter, setRequestFilter] = useState('');
    const [requestPayloadExpanded, setRequestPayloadExpanded] = useState(false);
    const [responseExpanded, setResponseExpanded] = useState(false);

    const filteredApiRequests = useMemo(() => {
      if (!requestFilter.trim()) return apiRequests;
      const filter = requestFilter.toLowerCase();
      return apiRequests.filter(
        (request) =>
          request.name.toLowerCase().includes(filter) ||
          request.url.toLowerCase().includes(filter) ||
          request.method.toLowerCase().includes(filter) ||
          String(request.status).includes(filter),
      );
    }, [apiRequests, requestFilter]);

    const selectedRequest = useMemo(
      () => apiRequests.find((req) => req.id === selectedRequestId) || null,
      [apiRequests, selectedRequestId],
    );

    return (
      <div className="flex h-full flex-col">
        <div className="shrink-0 space-y-3 border-b p-3 pt-0">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Filter by name, URL, method, or status…"
              value={requestFilter}
              onChange={(e) => setRequestFilter(e.target.value)}
              size="small"
              className="flex-1"
            />
            <span className="text-12 text-grey-11">
              {filteredApiRequests.length} of {apiRequests.length}
            </span>
            <div className="flex items-center justify-end">
              <Button
                type="button"
                size="small"
                appearance="ghost"
                onClick={useCallback(() => setApiRequests([]), [setApiRequests])}
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        <DataTableWithDetails
          leftPanelWidth={leftPanelWidth}
          setLeftPanelWidth={setLeftPanelWidth}
          headerColumns={[
            { label: 'Name', span: 3 },
            { label: 'Status', align: 'right' },
          ]}
          data={filteredApiRequests}
          selectedItem={selectedRequest}
          setSelectedItem={(request) => setSelectedRequestId(request?.id || null)}
          renderRow={(request) => (
            <div className="grid grid-cols-4 gap-2">
              <span className="col-span-3 truncate font-medium">{request.name}</span>
              <span
                className={classNames(
                  'text-right font-mono',
                  request.status >= 400 ? 'text-red-9' : request.status >= 300 ? 'text-yellow-9' : 'text-green-9',
                )}
              >
                {request.status}
              </span>
            </div>
          )}
          renderDetails={(request) => (
            <>
              <div className="space-y-2 text-12">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Name:</span>
                  <span className="ml-4 text-grey-11">{request.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Method:</span>
                  <span className="ml-4 text-grey-11">{request.method}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Status:</span>
                  <span className="ml-4 text-grey-11">{request.status}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Duration:</span>
                  <span className="ml-4 text-grey-11">{request.duration}ms</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">Timestamp:</span>
                  <span className="ml-4 text-grey-11">{request.timestamp.toLocaleTimeString()}</span>
                </div>
              </div>
              <div className="text-12">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-grey-9">URL:</span>
                  <span className="ml-4 max-w-xs truncate break-all font-mono text-grey-11">{request.url}</span>
                </div>
              </div>
              {!!request.requestData && (
                <DataSection
                  title="Request Payload"
                  data={request.requestData}
                  isExpanded={requestPayloadExpanded}
                  onToggleExpanded={() => setRequestPayloadExpanded(!requestPayloadExpanded)}
                  onCopy={onCopy}
                  copyTitle="Copy Request Payload"
                />
              )}
              {!!request.responseData && (
                <DataSection
                  title="Response"
                  data={request.responseData}
                  isExpanded={responseExpanded}
                  onToggleExpanded={() => setResponseExpanded(!responseExpanded)}
                  onCopy={onCopy}
                  copyTitle="Copy Response Data"
                />
              )}
            </>
          )}
          emptyMessage="No API requests captured"
          detailsTitle="Request Details"
          copyButtonText="Copy cURL"
          onCopy={(request) => onCopy(generateCurlCommand(request))}
        />
      </div>
    );
  },
);

APIRequestsTab.displayName = 'APIRequestsTab';
